import React from "react";
import { motion } from "framer-motion";

const TILES = [
  {
    id: "scope",
    code: "01",
    overline: "Scope",
    title: "From cosmos to concrete.",
    desc: "Every project has a horizon — orbital in ambition, granular in execution. We map both ends of the spectrum so nothing slips between the two.",
    video: "https://videos.pexels.com/video-files/3129671/3129671-uhd_2732_1440_30fps.mp4",
    img: "https://images.unsplash.com/photo-1462331940025-496dfbfc7564?auto=format&fit=crop&w=1200&q=70",
    alt: "Cosmos and stars — scope of vision",
    tag: "Vision",
  },
  {
    id: "structure",
    code: "02",
    overline: "Structure",
    title: "Steel, sequence, schedule.",
    desc: "Construction is choreography under gravity. Our schedules translate steel, crews, and trades into a rhythm your project can actually hold.",
    video: "https://videos.pexels.com/video-files/8721918/8721918-uhd_2560_1440_24fps.mp4",
    img: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=1200&q=70",
    alt: "Construction site with cranes and steel",
    tag: "Build",
  },
  {
    id: "stewardship",
    code: "03",
    overline: "Stewardship",
    title: "Built to outlast the build.",
    desc: "Infrastructure shapes landscapes for decades. Our controls protect that long arc — keeping costs, timelines, and environmental stakes in balance.",
    video: "https://videos.pexels.com/video-files/2169879/2169879-uhd_2560_1440_30fps.mp4",
    img: "https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?auto=format&fit=crop&w=1200&q=70",
    alt: "Nature and infrastructure landscape",
    tag: "Steward",
  },
];

export const Perspective = () => {
  return (
    <section
      data-testid="perspective-section"
      id="perspective"
      className="relative py-24 md:py-32 bg-[#020510] overflow-hidden"
    >
      <div className="absolute inset-0 grid-bg-fine opacity-30" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-16">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-10 h-px bg-[#FFB300]" />
            <span className="font-mono-accent text-[11px] text-[#FFB300]">
              / Project Perspective
            </span>
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#F1F5F9] leading-[1.05]">
            We see your project from{" "}
            <span className="text-[#00E5FF]">orbit</span> — and from{" "}
            <span className="text-[#FFB300]">ground level</span>.
          </h2>
          <p className="mt-6 text-[#94A3B8] text-base lg:text-lg leading-relaxed">
            Great project controls demand both the long view and the line-item.
            We hold both — scope, structure, and stewardship, every day.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-[#1E2A3B] border border-[#1E2A3B]">
          {TILES.map((t, i) => (
            <motion.article
              key={t.id}
              data-testid={`perspective-tile-${t.id}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.7, delay: i * 0.1 }}
              className="group relative bg-[#04070D] overflow-hidden"
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="auto"
                  poster={t.img}
                  className="absolute inset-0 w-full h-full object-cover grayscale-[40%] contrast-[1.05] brightness-[0.65] group-hover:grayscale-0 group-hover:brightness-[0.9] transition-all duration-700 ease-out scale-105 group-hover:scale-110"
                >
                  <source src={t.video} type="video/mp4" />
                </video>
                <div className="absolute inset-0 bg-gradient-to-t from-[#04070D] via-[#04070D]/30 to-transparent" />
                <div className="absolute inset-0 mix-blend-overlay bg-[#0A1A33]/40" />
                <div className="absolute top-4 left-4 flex items-center gap-2 font-mono-accent text-[10px] text-[#F1F5F9]">
                  <span className="w-1.5 h-1.5 bg-[#FFB300]" />
                  {t.code} / {t.tag}
                </div>
                <div className="absolute bottom-4 right-4 font-mono-accent text-[10px] text-[#00E5FF]">
                  {t.overline}
                </div>
              </div>
              <div className="p-8 lg:p-10">
                <h3 className="font-heading text-2xl text-[#F1F5F9] mb-4 leading-tight">
                  {t.title}
                </h3>
                <p className="text-sm text-[#94A3B8] leading-relaxed">{t.desc}</p>
              </div>
              <span className="absolute bottom-0 left-0 h-px w-0 bg-[#FFB300] group-hover:w-full transition-all duration-500" />
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Perspective;
